<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import {
  avatarShow,
  avatarCreate,
  avatarUpdate,
} from "@/common/api/rest/game/avatar";

import {
  imageList,
  imageCreate,
  imageDelete,
} from "@/common/api/rest/game/image";

import type { GameImageType } from "@/common/types/game/image";

import {
  baseConfirm,
  baseLoading,
  cropImage,
  svgIcon,
  viewImageList,
} from "@/common/components";

const { t } = useI18n();

const props = defineProps<{
  gameId: string;
}>();

const imageSize = "15rem";
const ratio = 1.333;

const images = ref<GameImageType[]>([]);
const avatar = ref<string | null>(null);
const selectedAvatar = ref<string | null>(null);
const selectedImage = ref<GameImageType | null>(null);
const selectedImageIndex = ref<number | null>(null);

const loading = ref(true);
const showImage = ref(false);
const showModalAddImage = ref(false);
const showModalDelImage = ref(false);

// images management
async function fetchImages() {
  await imageList(props.gameId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        images.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function imageShow(index: number) {
  selectedImageIndex.value = index;
  showImage.value = true;
}

function imageShowCancel() {
  selectedImageIndex.value = null;
  showImage.value = false;
}

function addImage() {
  showModalAddImage.value = true;
}

function addImageCancel() {
  showModalAddImage.value = false;
}

async function addImageConfirm(file: string) {
  showModalAddImage.value = false;

  store.commit("SET_LOADING", true);

  imageCreate(props.gameId, {
    file,
    status: "ACTIVE",
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchImages();
        addImageCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      showModalAddImage.value = true;
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function removeImage(item: GameImageType) {
  selectedImage.value = item;
  showModalDelImage.value = true;
}

function removeImageCancel() {
  selectedImage.value = null;
  showModalDelImage.value = false;
}

async function removeImageConfirm() {
  showModalDelImage.value = false;

  store.commit("SET_LOADING", true);

  await imageDelete(selectedImage.value.image_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchImages();
        removeImageCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

// avatar management

async function fetchAvatar() {
  await avatarShow(props.gameId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        avatar.value = data?.image_id ?? null;
        selectedAvatar.value = data?.image_id ?? null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAvatar(item: GameImageType) {
  store.commit("SET_LOADING", true);

  avatarCreate(props.gameId, item.image_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        avatar.value = item.image_id;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAvatar(item: GameImageType) {
  store.commit("SET_LOADING", true);

  avatarUpdate(props.gameId, item.image_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        avatar.value = item.image_id;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeAvatar(item: GameImageType) {
  if (avatar.value) {
    updateAvatar(item);
  } else {
    addAvatar(item);
  }
}

onMounted(async () => {
  await fetchImages();
  await fetchAvatar();
  loading.value = false;
});
</script>

<template>
  <section class="tab-console">
    <div class="content">
      <template v-if="loading">
        <div class="empty-data">
          <base-loading />
        </div>
      </template>
      <template v-else-if="images.length === 0">
        <div class="empty-data">
          <svgIcon icon="no-image" size="3rem" />
          <span>{{ t("collection.texts.no_images") }}</span>
        </div>
      </template>
      <template v-else>
        <div class="list">
          <template v-for="(item, index) in images" :key="item.image_id">
            <div class="image">
              <div class="avatar">
                <img :src="item.small" @click="imageShow(index)" />
              </div>
              <div class="actions">
                <div class="is-avatar">
                  <input
                    v-model="selectedAvatar"
                    type="radio"
                    name="avatar"
                    :id="`${item.image_id}${index}`"
                    :value="item.image_id"
                    @change="changeAvatar(item)"
                  />
                  <label :for="`${item.image_id}${index}`">
                    {{ t("common.labels.avatar") }}
                  </label>
                </div>

                <button
                  class="btn btn-transparent w-9 h-9 p-0"
                  @click="removeImage(item)"
                >
                  <svg-icon icon="trash" size="1.5rem" />
                </button>
              </div>
            </div>
          </template>
        </div>
      </template>

      <button class="btn btn-md btn-primary" @click="addImage()">
        <svg-icon icon="add" size="1.25rem" />
        <span>
          {{ t("collection.labels.image")?.toUpperCase() }}
        </span>
      </button>
    </div>

    <template v-if="showImage">
      <view-image-list
        :images="images.map((item) => item.large)"
        :selected="selectedImageIndex"
        @close="imageShowCancel"
      />
    </template>

    <template v-if="showModalAddImage">
      <crop-image
        @change="addImageConfirm($event)"
        @cancel="addImageCancel()"
      />
    </template>

    <base-confirm
      :show="showModalDelImage"
      :title="t('collection.modals.titles.del_image').toUpperCase()"
      :message="
        t('collection.modals.messages.del_image', {
          name: selectedImage?.title?.toUpperCase(),
        })
      "
      @cancel="removeImageCancel()"
      @confirm="removeImageConfirm()"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-console {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .images {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      padding: 1rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }

    .list {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: flex-start;
      width: 100%;
      gap: 1.5rem;
      row-gap: 1.5rem;
      padding: 1rem;
      background: var(--white-off);
      border-radius: 0.25rem;

      .image {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        background: white;
        border-radius: 0.5rem;
        padding: 1rem;
        margin-block-end: 1.5rem;

        .avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: v-bind("imageSize");
          height: calc(v-bind("imageSize") / v-bind("ratio"));
          min-width: 3rem;
          min-height: 3rem;
          color: var(--text-medium);
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            cursor: pointer;
          }
        }
      }
    }

    .actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .empty-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-block: 2rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-console {
    .content {
      padding-inline: 0;
    }
  }
}
</style>
